/** Shared helpers for org-wise and ownership-based service-period costing tables. */

/** Same dimension key as cost-analysis API. */
export const DIMENSION = 'service';
export const groupBy = { name: 'Service', id: DIMENSION };

/** Daily periodicity: max inclusive calendar days between start and end. */
export const DAILY_PERIODICITY_MAX_INCLUSIVE_DAYS = 60;

export const API_MIN_DATE = new Date('2023-01-01T00:00:00Z');

export function countInclusiveCalendarDays(start, end) {
    if (!start || !end || start > end) {
        return 0;
    }
    const s = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate()
    );
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
}

export function makeServiceCostingA11yProps(baseId) {
    return ({ index }) => ({
        id: `${baseId}-tab-${index}`,
        'aria-controls': `${baseId}-tabpanel-${index}`,
    });
}

export function ProcessCost(data) {
    if (data === null || data === undefined) {
        data = 0;
    }
    const num = Number(data);
    if (!Number.isFinite(num)) {
        return '0.000 $';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(3).toString() + 'k $';
    }
    return num.toFixed(3).toString() + ' $';
}

export function ParseDate(data) {
    if (data == null || typeof data !== 'string') {
        return '';
    }
    var date = data.split('-');
    if (date.length < 3 || !date[2] || date[2].length < 4) {
        return data;
    }
    return date[0] + '/' + date[1] + " '" + date[2][2] + date[2][3];
}

/** Weekly API columns look like "Week-3 01-01-2024 07-01-2024" (3+ space-separated tokens). Daily keys are a single DD-MM-YYYY. */
export function headerRowLooksWeekly(header) {
    if (!Array.isArray(header) || header.length < 2) {
        return false;
    }
    for (var i = 1; i < header.length; i++) {
        const h = header[i];
        if (typeof h !== 'string' || h.split(' ').filter(Boolean).length < 3) {
            return false;
        }
    }
    return true;
}

export function ProcessIncrement(data) {
    if (data === null || data === undefined) {
        data = 0;
    }
    if (data !== 'kickstart') {
        return Number(data).toFixed(2).toString() + '%';
    }
    return data;
}

export const TableDataCell = ({ cost, increment }) => {
    const incNum = increment === 'kickstart' ? increment : Number(increment);
    const showRed = increment !== 'kickstart' && incNum > 0;
    return (
        <div
            style={{ fontSize: '16px' }}
            className="w-[240px] h-[87px] py-4 font-medium text-gray-900 text-left diagonal-split-background"
        >
            <span className="sup">{ProcessCost(cost)}</span>
            {showRed ? (
                <span className="inf" style={{ color: 'red' }}>
                    {'+' + ProcessIncrement(increment)}
                </span>
            ) : (
                <span className="inf" style={{ color: 'green' }}>
                    {ProcessIncrement(increment)}
                </span>
            )}
        </div>
    );
};

export const TableFooterDataCell = ({ cost, increment }) => {
    const incNum = increment === 'kickstart' ? increment : Number(increment);
    const showRed = increment !== 'kickstart' && incNum > 0;
    return (
        <div
            style={{ fontSize: '16px', fontWeight: 900 }}
            className="w-[240px] h-[87px] py-4 text-sm font-medium text-white text-left diagonal-footer-split-background"
        >
            <span className="sup font-bold">{ProcessCost(cost)}</span>
            {showRed ? (
                <span className="inf font-bold" style={{ color: 'red' }}>
                    {'+' + ProcessIncrement(increment)}
                </span>
            ) : (
                <span className="inf font-bold" style={{ color: 'green' }}>
                    {ProcessIncrement(increment)}
                </span>
            )}
        </div>
    );
};

export const TableFirstDataCell = ({ children }) => (
    <div
        style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
        className="min-w-auto max-w-[335px] p-1 text-sm font-bold text-gray-900 text-center"
    >
        <h6>{children}</h6>
    </div>
);

export function parseDdMmYyyy(key) {
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec((key ?? '').trim());
    if (!m) {
        return null;
    }
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
}

/** Some APIs return daily columns as ISO YYYY-MM-DD instead of DD-MM-YYYY. */
export function parseIsoYyyyMmDd(key) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((key ?? '').trim());
    if (!m) {
        return null;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (
        dt.getFullYear() !== y ||
        dt.getMonth() !== mo ||
        dt.getDate() !== d
    ) {
        return null;
    }
    return dt;
}

/**
 * Weekly org-wise keys look like "Week-3 01-01-2024 07-01-2024" (week start/end as DD-MM-YYYY).
 * Sort by week start; also accepts any key containing a DD-MM-YYYY pair (first wins).
 */
export function parseWeeklyOrgWiseKey(key) {
    if (typeof key !== 'string') {
        return null;
    }
    const parts = key.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 3) {
        const start = parseDdMmYyyy(parts[1]);
        if (start) {
            return start;
        }
    }
    const m = /\b(\d{2}-\d{2}-\d{4})\b/g;
    let first = null;
    let match;
    while ((match = m.exec(key)) !== null) {
        const d = parseDdMmYyyy(match[1]);
        if (d) {
            first = d;
            break;
        }
    }
    return first;
}

/** Monthly org-wise keys are often English month names (not DD-MM-YYYY). */
const MONTH_NAME_TO_INDEX = {
    january: 0,
    jan: 0,
    february: 1,
    feb: 1,
    march: 2,
    mar: 2,
    april: 3,
    apr: 3,
    may: 4,
    june: 5,
    jun: 5,
    july: 6,
    jul: 6,
    august: 7,
    aug: 7,
    september: 8,
    sep: 8,
    sept: 8,
    october: 9,
    oct: 9,
    november: 10,
    nov: 10,
    december: 11,
    dec: 11,
};

/**
 * Parses keys like "Feb", "March", "April" or "Feb 2024". Year defaults so
 * month-only keys sort in calendar order (not alphabetical: April before Feb).
 */
export function parseMonthNamePeriodKey(key) {
    if (typeof key !== 'string') {
        return null;
    }
    const t = key.trim();
    const m = /^([A-Za-z]+)(?:\s+(\d{4}))?$/.exec(t);
    if (!m) {
        return null;
    }
    const mon = m[1].toLowerCase();
    const idx = MONTH_NAME_TO_INDEX[mon];
    if (idx === undefined) {
        return null;
    }
    const y = m[2] ? Number(m[2]) : 2000;
    if (!Number.isFinite(y)) {
        return null;
    }
    return new Date(y, idx, 1);
}

/** Single chronological ordering for daily, weekly, and monthly period column keys. */
export function parsePeriodKeyForSort(key) {
    return (
        parseDdMmYyyy(key) ??
        parseIsoYyyyMmDd(key) ??
        parseWeeklyOrgWiseKey(key) ??
        parseMonthNamePeriodKey(key) ??
        null
    );
}

export function sortDateKeys(keys) {
    return [...keys].sort((a, b) => {
        const da = parsePeriodKeyForSort(a);
        const db = parsePeriodKeyForSort(b);
        if (da && db) {
            return da - db;
        }
        return String(a).localeCompare(String(b));
    });
}

/** All period keys present on any row (excluding dimension), sorted like the table. */
export function collectSortedDateKeysFromRows(rows, dim) {
    const keySet = new Set();
    if (!Array.isArray(rows)) {
        return [];
    }
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row || typeof row !== 'object') {
            continue;
        }
        Object.keys(row).forEach((k) => {
            if (k !== dim) {
                keySet.add(k);
            }
        });
    }
    return sortDateKeys([...keySet]);
}

/**
 * Org-wise API rows can omit periods per service. The Cost pipeline assumes every row
 * has the same columns as data[0]; missing keys caused undefined tuples in the table.
 */
export function alignOrgWiseRowsToUnionColumns(rows, dim) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return rows;
    }
    const dateKeys = collectSortedDateKeysFromRows(rows, dim);
    return rows.map((row) => {
        if (!row || typeof row !== 'object') {
            return row;
        }
        const label =
            row[dim] != null && row[dim] !== '' ? String(row[dim]) : '';
        const out = { [dim]: label };
        for (let d = 0; d < dateKeys.length; d++) {
            const k = dateKeys[d];
            const v = Number(row[k]);
            out[k] = Number.isFinite(v) ? v : 0;
        }
        return out;
    });
}

/** API rows: { service, "DD-MM-YYYY": number, ... } → service first, dates sorted. */
export function normalizeCostAnalysisRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }
    return rows.map((row) => {
        if (!row || typeof row !== 'object') {
            return row;
        }
        const label =
            row[DIMENSION] != null && row[DIMENSION] !== ''
                ? String(row[DIMENSION])
                : '';
        const dateKeys = Object.keys(row).filter((k) => k !== DIMENSION);
        const numericKeys = dateKeys.filter((k) =>
            Number.isFinite(Number(row[k]))
        );
        const sorted = sortDateKeys(numericKeys);
        const out = { [DIMENSION]: label };
        for (const k of sorted) {
            out[k] = Number(row[k]);
        }
        return out;
    });
}

/** Label for the single row that sums every service except explicitly-separated tools. */
const CLUBBED_GCP_SERVICE_LABEL = 'GCP';

const SEPARATE_SERVICE_NAMES = new Set(['mongo', 'redis', 'elastic', 'kafka']);

/** Service column must match one of these keywords exactly (trimmed, case-insensitive). */
function isSeparateMongoRedisElasticService(name) {
    const n = String(name ?? '').trim().toLowerCase();
    return SEPARATE_SERVICE_NAMES.has(n) || n.startsWith('kafka');
}

/**
 * When enabled, merges every service row whose name is not in the separated set
 * into one "GCP" row (costs summed per period). Order: separate rows first, then GCP.
 */
export function clubOrgWiseOtherServices(rows, enabled) {
    if (!enabled || !Array.isArray(rows) || rows.length === 0) {
        return rows;
    }
    const dim = DIMENSION;
    const kept = [];
    const others = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const svc = row[dim];
        if (isSeparateMongoRedisElasticService(svc)) {
            kept.push(row);
        } else {
            others.push(row);
        }
    }
    if (others.length === 0) {
        return rows;
    }
    const merged = { [dim]: CLUBBED_GCP_SERVICE_LABEL };
    const periodKeys = collectSortedDateKeysFromRows(rows, dim);
    for (let d = 0; d < periodKeys.length; d++) {
        const k = periodKeys[d];
        let sum = 0;
        for (let o = 0; o < others.length; o++) {
            const v = Number(others[o][k]);
            sum += Number.isFinite(v) ? v : 0;
        }
        merged[k] = sum;
    }
    return [...kept, merged];
}

export const periodicities = [
    { name: 'Daily', id: 'daily' },
    { name: 'Weekly', id: 'weekly' },
    { name: 'Monthly', id: 'monthly' },
];
